import { c as _c } from "react/compiler-runtime";
import { useContext, useMemo, useRef, useState } from 'react';
import { ApiKey } from '../../../../../../../../../typings/ApiKey.interface';
import Button from '../../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../../common/base/Card';
import Icon from '../../../../../common/base/Icon';
import Text from '../../../../../common/base/Text';
import Tooltip from '../../../../../common/base/Tooltip';
import CopyableField from '../../../../../common/CopyableField';
import { Div } from '../../../../../common/helpers/StyledUtils';
import { DashboardContext } from '../../../DashboardContext';
import { ApiKeyActionsMenu } from '../../../../../common/ApiKeys/ApiKeyActionsMenu';
import { ApiKeyLabelCell, ApiKeyPermissionsBadge } from '../../../../../common/ApiKeys/ApiKeyLabelCell';
import { UnauthorizedApiKeyRow } from '../../../../../common/ApiKeys/UnauthorizedApiKeyRow';
import Divider from '../../../../../common/base/Divider';
interface ProjectApiKeysTableProps {
  api_keys: ApiKey[];
  team_id?: string;
  // Full, unfiltered count of this table's keys. When provided, the body height is fixed to this
  // count (capped by the page size) so searching/filtering doesn't make the table jump around.
  total?: number;
  // The viewer can't list API keys (the server returns 401/403). Render the same table shell with
  // a single masked, disabled placeholder row instead of real data.
  unauthorized?: boolean;
  unauthorized_tooltip?: string;
  onEditKey: (api_key: ApiKey) => void;
  onRollKey: (api_key: ApiKey) => void;
  onDeleteKey: (api_key: ApiKey) => void;
}

// The Organization API keys table's Name track (196px) splits here into Name + Project
// (86px + 12px gap + 86px), so the Permissions and Key columns of both tables line up
// when they're stacked on the page. Tracks + 12px gaps reproduce the design's
// 104px-per-column rhythm.
const grid_values_multi = {
  columns: 'minmax(0, 86px) minmax(0, 86px) minmax(0, 80px) 1fr',
  gap: 3
};
const grid_values_single = {
  columns: 'minmax(0, 184px) minmax(0, 80px) 1fr',
  gap: 3
};
const PAGE_SIZE = 10;
const ROW_HEIGHT = 45;
const MANAGE_UNAUTHORIZED_TOOLTIP = 'Only project admins can edit, roll or delete API keys.';

// Project name that shows a tooltip with the full name, but only when it is actually
// ellipsed. The tooltip is fully controlled: overflow is measured when the hover would
// open it, so layout changes never leave a stale flag.
function ProjectNameCell(t0) {
  const $ = _c(9);
  const {
    project_name
  } = t0;
  const name_ref = useRef(null);
  const [show_tooltip, setShowTooltip] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      y: 3
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const node = name_ref.current;
      setShowTooltip(!!node && node.scrollWidth > node.clientWidth);
    };
    t3 = () => setShowTooltip(false);
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== project_name) {
    t4 = <Text size="s" ellipsis={true} ref={name_ref}>{project_name}</Text>;
    $[3] = project_name;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== project_name || $[6] !== show_tooltip || $[7] !== t4) {
    t5 = <Div min_w={0} p={t1}><Tooltip fill={true} side="bottom" tooltip={project_name} open={show_tooltip} onHover={t2} onLeave={t3}>{t4}</Tooltip></Div>;
    $[5] = project_name;
    $[6] = show_tooltip;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
interface ProjectApiKeyRowProps {
  api_key: ApiKey;
  project_name: string;
  show_project_column: boolean;
  grid_values: {
    columns: string;
  };
  onEditKey: (api_key: ApiKey) => void;
  onRollKey: (api_key: ApiKey) => void;
  onDeleteKey: (api_key: ApiKey) => void;
}
function ProjectApiKeyRow(t0) {
  const $ = _c(33);
  const {
    api_key,
    project_name,
    show_project_column,
    grid_values,
    onEditKey,
    onRollKey,
    onDeleteKey
  } = t0;
  const [is_secret, setIsSecret] = useState(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 4
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== api_key) {
    t2 = <Div min_w={0}><ApiKeyLabelCell api_key={api_key} ellipsis={true} hide_permissions={true} /></Div>;
    $[1] = api_key;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== project_name || $[4] !== show_project_column) {
    t3 = show_project_column && <ProjectNameCell project_name={project_name} />;
    $[3] = project_name;
    $[4] = show_project_column;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      align: "center"
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== api_key) {
    t5 = <Div min_w={0} flex={t4}><ApiKeyPermissionsBadge api_key={api_key} /></Div>;
    $[7] = api_key;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      y: 2
    };
    t7 = {
      align: "center",
      gap: 1
    };
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      grow: true
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== api_key.key || $[13] !== is_secret) {
    t9 = <CopyableField mono={true} ghost={true} text_size="xs" flex={t8} value={api_key.key} secret={is_secret} reveal_on_hover={false} visible_values={10} />;
    $[12] = api_key.key;
    $[13] = is_secret;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = is_secret ? "view" : "dismiss";
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setIsSecret(_temp);
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10) {
    t12 = <Button icon={t10} ghost={true} onClick={t11} />;
    $[16] = t10;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== api_key || $[19] !== onDeleteKey || $[20] !== onEditKey || $[21] !== onRollKey) {
    t13 = <ApiKeyActionsMenu api_key={api_key} onEditKey={onEditKey} onRollKey={onRollKey} onDeleteKey={onDeleteKey} ghost={true} required_role="admin" unauthorized_tooltip={MANAGE_UNAUTHORIZED_TOOLTIP} />;
    $[18] = api_key;
    $[19] = onDeleteKey;
    $[20] = onEditKey;
    $[21] = onRollKey;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t12 || $[24] !== t13 || $[25] !== t9) {
    t14 = <Div min_w={0}><Div p={t6} flex={t7}>{t9}{t12}{t13}</Div></Div>;
    $[23] = t12;
    $[24] = t13;
    $[25] = t9;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== grid_values || $[28] !== t14 || $[29] !== t2 || $[30] !== t3 || $[31] !== t5) {
    t15 = <StyledCardSection p={t1} grid={grid_values}>{t2}{t3}{t5}{t14}</StyledCardSection>;
    $[27] = grid_values;
    $[28] = t14;
    $[29] = t2;
    $[30] = t3;
    $[31] = t5;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  return t15;
}
function _temp(prev) {
  return !prev;
}
export function ProjectApiKeysTable(t0) {
  const $ = _c(53);
  const {
    api_keys,
    team_id,
    total,
    unauthorized,
    unauthorized_tooltip,
    onEditKey,
    onRollKey,
    onDeleteKey
  } = t0;
  const {
    organization,
    teams
  } = useContext(DashboardContext);
  const [page, setPage] = useState(1);
  const show_project_column = !team_id;
  const grid_values = show_project_column ? grid_values_multi : grid_values_single;
  organization?.id;
  let t1;
  if ($[0] !== organization?.id || $[1] !== teams) {
    let t2;
    if ($[3] !== organization?.id) {
      t2 = team => team.organization_id === organization?.id;
      $[3] = organization?.id;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = new Map((teams ?? []).filter(t2).map(_temp2));
    $[0] = organization?.id;
    $[1] = teams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const team_name_by_id = t1;
  let t2;
  if ($[5] !== api_keys || $[6] !== team_id) {
    bb0: {
      const keys = api_keys.filter(_temp3);
      if (team_id) {
        let t3;
        if ($[8] !== team_id) {
          t3 = api_key_0 => api_key_0.team_id === team_id;
          $[8] = team_id;
          $[9] = t3;
        } else {
          t3 = $[9];
        }
        t2 = keys.filter(t3);
        break bb0;
      }
      t2 = keys;
    }
    $[5] = api_keys;
    $[6] = team_id;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const project_api_keys = t2;
  const total_count = total ?? project_api_keys.length;
  const has_pagination = total_count > PAGE_SIZE;
  const body_height = total !== undefined ? Math.min(total_count, PAGE_SIZE) * ROW_HEIGHT : has_pagination ? PAGE_SIZE * ROW_HEIGHT : undefined;
  let t3;
  if ($[10] !== body_height) {
    t3 = body_height !== undefined ? {
      px: body_height
    } : undefined;
    $[10] = body_height;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const body_h = t3;
  const page_count = Math.max(1, Math.ceil(project_api_keys.length / PAGE_SIZE));
  const current_page = Math.min(page, page_count);
  let t4;
  if ($[12] !== current_page || $[13] !== has_pagination || $[14] !== project_api_keys) {
    t4 = has_pagination ? project_api_keys.slice((current_page - 1) * PAGE_SIZE, current_page * PAGE_SIZE) : project_api_keys;
    $[12] = current_page;
    $[13] = has_pagination;
    $[14] = project_api_keys;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const paginated_api_keys = t4;
  let t5;
  if ($[16] !== team_name_by_id) {
    t5 = api_key_1 => {
      const team_name = team_name_by_id.get(api_key_1.team_id);
      return team_name ?? "Project";
    };
    $[16] = team_name_by_id;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const getProjectName = t5;
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      x: 4
    };
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      grow: false
    };
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Div flex={t7}><Text muted={true} semi={true} size="xs" p={{
        y: 1
      }}>Name</Text></Div>;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== show_project_column) {
    t9 = show_project_column && <Div flex={{
      grow: false
    }}><Text muted={true} semi={true} size="xs" p={{
        y: 1
      }}>Project</Text></Div>;
    $[21] = show_project_column;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      grow: false
    };
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Div flex={t10}><Text muted={true} semi={true} size="xs" p={{
        y: 1
      }}>Permissions</Text></Div>;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      grow: true,
      justify: "space-between"
    };
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Div flex={t12}><Text muted={true} semi={true} size="xs" p={{
        y: 1
      }}>Key</Text></Div>;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== grid_values || $[28] !== t9) {
    t14 = <StyledCardSection muted={true} p={t6}><Div grid={grid_values}>{t8}{t9}{t11}{t13}</Div></StyledCardSection>;
    $[27] = grid_values;
    $[28] = t9;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== body_h || $[31] !== getProjectName || $[32] !== grid_values || $[33] !== onDeleteKey || $[34] !== onEditKey || $[35] !== onRollKey || $[36] !== paginated_api_keys || $[37] !== show_project_column || $[38] !== team_id || $[39] !== total_count || $[40] !== unauthorized || $[41] !== unauthorized_tooltip) {
    t15 = unauthorized ? <UnauthorizedApiKeyRow show_project_column={show_project_column} grid_values={grid_values} tooltip={unauthorized_tooltip} data-testid="project-api-keys-unauthorized-row" /> : total_count === 0 ? <StyledCardSection p={3}><Text muted={true}><Icon icon="info" muted={true} left={true} />{team_id ? "No API keys yet." : "No project API keys yet."}</Text></StyledCardSection> : paginated_api_keys.length === 0 ? <StyledCardSection h={body_h} flex={{
      align: "center",
      justify: "center"
    }}><Text muted={true}><Icon icon="info" muted={true} left={true} />No matching API keys.</Text></StyledCardSection> : <StyledCardSection h={body_h}>{paginated_api_keys.map(api_key_2 => <ProjectApiKeyRow key={api_key_2.id} api_key={api_key_2} project_name={getProjectName(api_key_2)} show_project_column={show_project_column} grid_values={grid_values} onEditKey={onEditKey} onRollKey={onRollKey} onDeleteKey={onDeleteKey} />)}{paginated_api_keys.length < Math.min(total_count, PAGE_SIZE) && <Divider />}</StyledCardSection>;
    $[30] = body_h;
    $[31] = getProjectName;
    $[32] = grid_values;
    $[33] = onDeleteKey;
    $[34] = onEditKey;
    $[35] = onRollKey;
    $[36] = paginated_api_keys;
    $[37] = show_project_column;
    $[38] = team_id;
    $[39] = total_count;
    $[40] = unauthorized;
    $[41] = unauthorized_tooltip;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== current_page || $[44] !== has_pagination || $[45] !== page_count || $[46] !== paginated_api_keys || $[47] !== project_api_keys.length) {
    t16 = has_pagination && <StyledCardSection><Div p={{
        x: 4,
        y: 3
      }} flex={{
        justify: "space-between",
        align: "center"
      }}><Text muted={true} size="s">Viewing {paginated_api_keys.length} of {project_api_keys.length}</Text><Div flex={{
          align: "center",
          gap: 2
        }}><Button onClick={() => setPage(Math.max(1, current_page - 1))} outline={true} icon="chevron_left" disabled={current_page <= 1} /><Button onClick={() => setPage(Math.min(page_count, current_page + 1))} outline={true} icon="chevron_right" disabled={current_page >= page_count} /></Div></Div></StyledCardSection>;
    $[43] = current_page;
    $[44] = has_pagination;
    $[45] = page_count;
    $[46] = paginated_api_keys;
    $[47] = project_api_keys.length;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t14 || $[50] !== t15 || $[51] !== t16) {
    t17 = <StyledCard overflow_hidden={true}>{t14}{t15}{t16}</StyledCard>;
    $[49] = t14;
    $[50] = t15;
    $[51] = t16;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  return t17;
}
function _temp3(api_key) {
  return api_key.team_id;
}
function _temp2(team_0) {
  return [team_0.id, team_0.name];
}